import React from 'react';
import { InputText } from 'primereact/inputtext';
import { AutoComplete } from 'primereact/autocomplete';

export interface ProducerAgent {
  databaseId: string;
  firstName: string;
  lastName: string;
  order: number;
}

export interface ProducerLocation {
  id: string;
  locationId: string;
  businessName: string | null;
  adressLine1: string | null;
  adressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  type: string;
  agents?: ProducerAgent[];
}

/**
 * The producer/contact block follows the first *selected policy that has an agent*, in
 * selection order — not strictly the first selection. A policy with no agent in NowCerts
 * (e.g. Trucking Co. 1325413251) would otherwise leave the block blank even when the
 * agent's other policies are on the same certificate. Returns the empty contact when none
 * of the policies has one.
 */
export async function fetchFirstPolicyAgentContact(policyDatabaseIds: string[]): Promise<{ agentId: string; name: string; phone: string; fax: string; email: string }> {
  let last = { agentId: '', name: '', phone: '', fax: '', email: '' };
  for (const id of policyDatabaseIds) {
    last = await fetchPolicyAgentContact(id);
    if (last.agentId && last.name) return last;
  }
  return last;
}

export async function fetchPolicyAgentContact(policyDatabaseId: string): Promise<{ agentId: string; name: string; phone: string; fax: string; email: string }> {
  const ncToken = localStorage.getItem('NCToken') || '';
  const igToken = localStorage.getItem('igtoken') || '';
  try {
    const res = await fetch(
      `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/policy-agents/${encodeURIComponent(policyDatabaseId)}`,
      {
        headers: {
          NCToken: ncToken,
          Authorization: `Bearer ${igToken}`,
          Accept: 'application/json',
        },
      }
    );
    if (!res.ok) return { agentId: '', name: '', phone: '', fax: '', email: '' };
    const data = await res.json();
    // NowCerts returns an array of agent objects
    const agent = Array.isArray(data) ? data[0] : data;
    // Strip extension text (e.g. "123-344-5566 Ext. 1122") — keep only the base number
    const stripExt = (val: string) => val ? val.split(/\s*[Ee]xt\.?\s*/)[0].trim() : '';
    return {
      agentId: agent?.databaseId || agent?.id || '',
      name: [agent?.firstName, agent?.lastName].filter(Boolean).join(' ') || agent?.name || '',
      phone: stripExt(agent?.phone || agent?.cellPhone || ''),
      fax: stripExt(agent?.fax || ''),
      email: agent?.email || '',
    };
  } catch {
    return { agentId: '', name: '', phone: '', fax: '', email: '' };
  }
}

export async function fetchAgentContact(agentId: string): Promise<{ npnNumber: string; phone: string; fax: string; email: string; addressLine1: string; city: string; state: string; zipCode: string }> {
  const ncToken = localStorage.getItem('NCToken') || '';
  const igToken = localStorage.getItem('igtoken') || '';
  try {
    const res = await fetch(
      `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/agent/${encodeURIComponent(agentId)}`,
      {
        headers: {
          NCToken: ncToken,
          Authorization: `Bearer ${igToken}`,
          Accept: 'application/json',
        },
      }
    );
    if (!res.ok) return { npnNumber: '', phone: '', fax: '', email: '', addressLine1: '', city: '', state: '', zipCode: '' };
    const data = await res.json();
    const agent = Array.isArray(data?.value) ? data.value[0] : data;
    return {
      npnNumber: agent?.npnNumber || '',
      phone: agent?.phone || '',
      fax: agent?.fax || '',
      email: agent?.email || '',
      addressLine1: agent?.addressLine1 || '',
      city: agent?.city || '',
      state: agent?.state || '',
      zipCode: agent?.zipCode || '',
    };
  } catch {
    return { npnNumber: '', phone: '', fax: '', email: '', addressLine1: '', city: '', state: '', zipCode: '' };
  }
}

export function useProducers(agencyLocationId?: string) {
  const [producers, setProducers] = React.useState<ProducerLocation[]>([]);
  const [producersLoading, setProducersLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const fetchProducers = async () => {
      setProducersLoading(true);
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const authToken = localStorage.getItem('igtoken') || '';
        const params = new URLSearchParams({ top: '100', skip: '0' });
        if (agencyLocationId) {
          params.append('agencyLocationId', agencyLocationId);
        }
        const res = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/producers?${params.toString()}`,
          {
            headers: {
              NCToken: ncToken,
              Authorization: `Bearer ${authToken}`,
              Accept: 'application/json',
            },
          }
        );
        const data = await res.json();
        if (!cancelled && Array.isArray(data.value)) {
          setProducers(data.value);
        }
      } catch {
      } finally {
        if (!cancelled) setProducersLoading(false);
      }
    };
    fetchProducers();
    return () => { cancelled = true; };
  }, [agencyLocationId]);

  const producerOptions = producers.map((p) => ({
    label: `${p.locationId || p.adressLine1 || ''} (${[p.adressLine1, p.city, p.state, p.zip].filter(Boolean).join(', ')})`,
    value: p.id,
  }));

  return { producers, producersLoading, producerOptions };
}

interface ProducerSectionProps {
  title?: string;
  address: string;
  onAddressChange: (v: string) => void;
  city: string;
  onCityChange: (v: string) => void;
  street: string;
  onStreetChange: (v: string) => void;
  state: string;
  onStateChange: (v: string) => void;
  zip: string;
  onZipChange: (v: string) => void;
  onProducerSelect?: (p: ProducerLocation) => void;
  onAgentContact?: (phone: string, fax: string, email: string) => void;
  readOnly?: boolean;
}

export default function ProducerSection({
  title = 'PRODUCER',
  address,
  onAddressChange,
  city,
  onCityChange,
  street,
  onStreetChange,
  state,
  onStateChange,
  zip,
  onZipChange,
  onProducerSelect,
  onAgentContact,
  readOnly = false,
}: ProducerSectionProps) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="masterCert__sectionHeader">{title}</div>
      <InputText
        value={address}
        onChange={(e) => onAddressChange(e.target.value)}
        placeholder="Address"
        className="masterCert__input masterCert__inputFull masterCert__inputMbSm"
        readOnly={readOnly}
      />
      <InputText
        value={street}
        onChange={(e) => onStreetChange(e.target.value)}
        placeholder="Address Line 1"
        className="masterCert__input masterCert__inputFull masterCert__inputMbSm"
        readOnly={readOnly}
      />
      <div className="masterCert__addressGrid">
        <InputText
          value={city}
          onChange={(e) => onCityChange(e.target.value)}
          placeholder="City"
          className="masterCert__input masterCert__inputFull"
          readOnly={readOnly}
        />
        <InputText
          value={state}
          onChange={(e) => onStateChange(e.target.value)}
          placeholder="State"
          className="masterCert__input masterCert__inputFull"
          readOnly={readOnly}
        />
        <InputText
          value={zip}
          onChange={(e) => onZipChange(e.target.value)}
          placeholder="Zip"
          className="masterCert__input masterCert__inputFull"
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}
